const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

/**
 * Calculate route using Mapbox Directions API
 * @param {[number,number]} origin - [lng, lat]
 * @param {[number,number]} destination - [lng, lat]
 * @param {Array<[number,number]>} waypoints - [[lng, lat], ...]
 * @returns {Object} { distance (m), duration (s), geometry, legs }
 */
export async function calculateRoute(origin, destination, waypoints = [], options = {}) {
  if (!MAPBOX_TOKEN || MAPBOX_TOKEN.includes('PLACEHOLDER')) {
    return { route: mockRoute(origin, destination), alternatives: [] };
  }

  const coords = [origin, ...waypoints, destination]
    .map(c => `${c[0]},${c[1]}`)
    .join(';');

  const excludeParts = [];
  if (options.avoidTolls) excludeParts.push('toll');
  if (options.avoidFerries) excludeParts.push('ferry');
  if (options.avoidMotorways) excludeParts.push('motorway');
  if (options.avoidUnpaved) excludeParts.push('unpaved');

  const excludeParam = excludeParts.length > 0 ? `&exclude=${excludeParts.join(',')}` : '';
  const altParam = options.alternatives ? '&alternatives=true' : '';

  const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${coords}` +
    `?geometries=geojson&overview=full&steps=true${excludeParam}${altParam}&access_token=${MAPBOX_TOKEN}`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Mapbox API error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  if (!data.routes || data.routes.length === 0) {
    throw new Error('Nenhuma rota encontrada entre os pontos indicados');
  }

  function classifyRoad(ref, name) {
    // Split multiple refs: "A-62; E 80" → ["A-62", "E80"]
    const refs = (ref || '').toUpperCase().split(/[;,]+/).map(r => r.replace(/\s+/g, '').trim()).filter(Boolean);
    const priority = { motorway: 4, highway: 3, national: 2, local: 1 };
    let best = null;
    for (const r of refs) {
      let type = null;
      if (/^A-?\d+$/.test(r) || /^AP-?\d+$/.test(r) || /^AG-?\d+$/.test(r)) type = 'motorway'; // A1, A-1, A-62, AP-7 (PT+ES)
      else if (/^E-?\d+$/.test(r))          type = 'motorway'; // E80, E-80 rotas europeias
      else if (/^(IP|IC)-?\d+$/.test(r))    type = 'highway';  // IC19, IP1 (PT)
      else if (/^(EN|EM|ER|CM|M)-?\d+/.test(r) || /^N-?\d+$/.test(r)) type = 'national'; // EN10, N-1 (PT+ES)
      if (type && priority[type] > (priority[best] || 0)) best = type;
    }
    if (best) return best;
    // Fallback: keywords no nome da estrada
    const n = (name || '').toUpperCase();
    if (/AUTO.?ESTRADA|AUTOPISTA|AUTOV[IÍ]A/.test(n)) return 'motorway';
    if (/ITINER[AÁ]RIO|VARIANTE/.test(n))             return 'highway';
    if (/ESTRADA (NACIONAL|MUNICIPAL|REGIONAL)/.test(n)) return 'national';
    return 'local';
  }

  function parseSteps(legs) {
    const steps = legs.flatMap(leg => leg.steps || []);
    const roadBreakdown = { motorway: 0, highway: 0, national: 0, local: 0 };
    const routeSteps = [];
    let prev = null;

    steps.forEach(step => {
      if (!step.distance || step.distance < 1) return;
      const ref = step.ref || '';
      const name = step.name || '';
      const label = ref || name || 'Sem nome';
      const type = classifyRoad(ref, name);
      roadBreakdown[type] += step.distance;

      if (prev && prev.label === label) {
        prev.distance += step.distance;
      } else {
        prev = { label, ref, name, type, distance: step.distance, instruction: step.maneuver?.instruction || '' };
        routeSteps.push(prev);
      }
    });

    return { roadBreakdown, routeSteps };
  }

  function parseRoute(r) {
    const { roadBreakdown, routeSteps } = parseSteps(r.legs || []);
    return { distance: r.distance, duration: r.duration, geometry: r.geometry, legs: r.legs, roadBreakdown, routeSteps };
  }

  return {
    route: parseRoute(data.routes[0]),
    alternatives: data.routes.slice(1).map(parseRoute),
  };
}

/**
 * Reverse geocode coordinates to address
 */
export async function reverseGeocode(lng, lat) {
  if (!MAPBOX_TOKEN || MAPBOX_TOKEN.includes('PLACEHOLDER')) {
    return `${lat.toFixed(4)}°N, ${lng.toFixed(4)}°E`;
  }

  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json` +
    `?language=pt&access_token=${MAPBOX_TOKEN}`;

  try {
    const response = await fetch(url);
    const data = await response.json();
    if (data.features && data.features.length > 0) {
      return data.features[0].place_name;
    }
  } catch (e) {
    // ignore
  }
  return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
}

/**
 * Reverse geocode to city/place name only (for tachograph stops)
 */
export async function reverseGeocodePlace(lng, lat) {
  if (!MAPBOX_TOKEN || MAPBOX_TOKEN.includes('PLACEHOLDER')) return null;
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json` +
    `?language=pt&types=place,locality,district&limit=1&access_token=${MAPBOX_TOKEN}`;
  try {
    const response = await fetch(url);
    const data = await response.json();
    if (data.features && data.features.length > 0) {
      return data.features[0].text; // just city/municipality name
    }
  } catch { /* ignore */ }
  return null;
}

/**
 * Forward geocode: search for places by text query
 * Returns [{ name, coordinates: [lng, lat] }]
 */
export async function geocodeSearch(query) {
  if (!MAPBOX_TOKEN || MAPBOX_TOKEN.includes('PLACEHOLDER') || !query || query.length < 2) {
    return [];
  }

  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json` +
    `?language=pt&types=place,region,address,poi&autocomplete=true&limit=5&access_token=${MAPBOX_TOKEN}`;

  try {
    const response = await fetch(url);
    const data = await response.json();
    if (data.features) {
      return data.features.map(f => ({
        name: f.place_name,
        coordinates: f.center, // [lng, lat]
      }));
    }
  } catch (e) {
    // ignore
  }
  return [];
}

const HERE_TOKEN = process.env.REACT_APP_HERE_TOKEN;

// ISO-2 → ISO-3 mapping for HERE (which uses 3-letter codes). Covers
// the operational countries we care about; falls back to no-bias for
// unknown codes.
const ISO2_TO_ISO3 = {
  PT: 'PRT', ES: 'ESP', FR: 'FRA', GB: 'GBR', IE: 'IRL',
  DE: 'DEU', IT: 'ITA', NL: 'NLD', BE: 'BEL', LU: 'LUX',
  CH: 'CHE', AT: 'AUT', PL: 'POL', AO: 'AGO', BR: 'BRA',
  MZ: 'MOZ', CV: 'CPV',
};

// Country centroid (lng, lat) used as `proximity` bias so the operator's
// search ("rua do Almada") preferentially returns matches inside the
// home country before drifting to lookalikes abroad.
const COUNTRY_CENTROID = {
  PT: [-8.5, 39.5],
  ES: [-3.7, 40.4],
  FR: [2.35, 46.6],
  GB: [-1.5, 53.0],
  IE: [-8.0, 53.4],
  AO: [17.0, -12.5],
  BR: [-51.9, -14.2],
  MZ: [35.5, -18.7],
};

/**
 * Multi-provider geocode search (Mapbox + HERE + Nominatim)
 * Searches all providers in parallel and merges results (deduplicated).
 *
 * @param {string} query
 * @param {object} [options]
 * @param {string} [options.country] — ISO-2 (PT/ES/FR…). Biases all
 *   providers to that country and ranks in-country hits first. Falls
 *   back to global search if not provided.
 * @returns {Array<{ name, coordinates: [lng, lat], source }>}
 */
export async function geocodeSearchMulti(query, options = {}) {
  if (!query || query.length < 2) return [];

  const country2 = (options.country || '').toUpperCase();
  const country3 = ISO2_TO_ISO3[country2] || null;
  const proximity = COUNTRY_CENTROID[country2] || null;

  const providers = [];

  // 1. Mapbox — `country=pt` restricts strictly; `proximity=lng,lat`
  //    nudges ranking. `routing=true` faz com que para endereços a
  //    coord devolvida seja o ponto de acesso (porta) e não o centróide
  //    da parcela — relevante quando o motorista vai navegar para o
  //    sítio. `address` à frente nos types para favorecer matches de
  //    rua + número antes de POIs genéricos.
  if (MAPBOX_TOKEN && !MAPBOX_TOKEN.includes('PLACEHOLDER')) {
    const params = new URLSearchParams({
      language: 'pt',
      types: 'address,poi,place,region',
      autocomplete: 'true',
      routing: 'true',
      limit: '10',
      access_token: MAPBOX_TOKEN,
    });
    if (country2) params.set('country', country2.toLowerCase());
    if (proximity) params.set('proximity', `${proximity[0]},${proximity[1]}`);
    providers.push(
      fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?${params}`)
        .then(r => r.json())
        .then(data => (data.features || []).map(f => ({
          name: f.place_name,
          coordinates: f.center,
          source: 'mapbox',
          country: f.context?.find(c => c.id?.startsWith('country.'))?.short_code?.toUpperCase() || null,
          isParcel: Array.isArray(f.place_type) && f.place_type.includes('address'),
        })))
        .catch(() => [])
    );
  }

  // 2. HERE Geocoding — `in=countryCode:PRT` restricts to country.
  if (HERE_TOKEN) {
    const hp = new URLSearchParams({
      q: query,
      lang: 'pt-PT',
      limit: '8',
      apiKey: HERE_TOKEN,
    });
    if (country3) hp.set('in', `countryCode:${country3}`);
    if (proximity) hp.set('at', `${proximity[1]},${proximity[0]}`);
    providers.push(
      fetch(`https://geocode.search.hereapi.com/v1/geocode?${hp}`)
        .then(r => r.json())
        .then(data => (data.items || []).map(item => ({
          name: item.title + (item.address?.city ? `, ${item.address.city}` : '') +
                (item.address?.countryName ? `, ${item.address.countryName}` : ''),
          coordinates: [item.position.lng, item.position.lat],
          source: 'here',
          country: item.address?.countryCode || null,
        })))
        .catch(() => [])
    );
  }

  // 3. Nominatim — `countrycodes=pt` restricts; `viewbox`+`bounded` nudges.
  const np = new URLSearchParams({
    q: query, format: 'json', addressdetails: '1', limit: '8',
    'accept-language': 'pt',
  });
  if (country2) np.set('countrycodes', country2.toLowerCase());
  providers.push(
    fetch(`https://nominatim.openstreetmap.org/search?${np}`, {
      headers: { 'User-Agent': 'EcoPlanner/1.0' },
    })
      .then(r => r.json())
      .then(data => (data || []).map(item => ({
        name: item.display_name,
        coordinates: [parseFloat(item.lon), parseFloat(item.lat)],
        source: 'osm',
        country: item.address?.country_code?.toUpperCase() || null,
      })))
      .catch(() => [])
  );

  // 4. Google Places (Text Search v1) — best-in-class POI database for
  //    local businesses (pastelarias, restaurantes, bombas de gasolina,
  //    cafés, lojas, hotéis). Uses the same Maps key already configured
  //    for Maps JS / Street View / Routes. Requires the "Places API
  //    (New)" to be enabled on the Google Cloud project — without it,
  //    the call returns 403 and is silently skipped, so geocoding still
  //    works via the other 3 providers.
  const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
  if (GOOGLE_KEY && !GOOGLE_KEY.includes('PLACEHOLDER')) {
    const body = {
      textQuery: query,
      languageCode: 'pt',
      maxResultCount: 8,
    };
    if (country2) body.regionCode = country2;
    if (proximity) {
      body.locationBias = {
        circle: {
          center: { latitude: proximity[1], longitude: proximity[0] },
          radius: 50000.0,
        },
      };
    }
    providers.push(
      fetch('https://places.googleapis.com/v1/places:searchText', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': GOOGLE_KEY,
          'X-Goog-FieldMask': 'places.displayName,places.formattedAddress,places.location,places.types,places.addressComponents',
        },
        body: JSON.stringify(body),
      })
        .then(r => r.ok ? r.json() : { places: [] })
        .then(data => (data.places || []).map(p => {
          const cc = (p.addressComponents || [])
            .find(c => Array.isArray(c.types) && c.types.includes('country'))
            ?.shortText?.toUpperCase() || null;
          const display = (p.displayName?.text || '').trim();
          const addr = (p.formattedAddress || '').trim();
          // Compose label as "Nome do sítio · Morada" so the operator
          // sees both "Pastelaria Versailles" and "R. da Junqueira ...".
          const label = display && addr && !addr.includes(display)
            ? `${display} · ${addr}`
            : (display || addr);
          return {
            name: label,
            coordinates: [p.location.longitude, p.location.latitude],
            source: 'google',
            country: cc,
          };
        }))
        .catch(() => [])
    );
  }

  // 5. Google Geocoding API — diferente do Places (acima). Esta é a
  //    engine de coordenadas exactas que o Google Maps usa para
  //    navegação: devolve formatted_address completo (rua + número +
  //    código postal + localidade + país) e a coordenada à porta da
  //    parcela. Crítico para o nosso caso de uso porque o motorista
  //    precisa de chegar exactamente ao endereço, não ao centróide do
  //    arruamento. Mantém-se separada do Places para que as duas
  //    fontes Google possam aparecer em paralelo nas sugestões (POI vs
  //    morada precisa).
  if (GOOGLE_KEY && !GOOGLE_KEY.includes('PLACEHOLDER')) {
    const gp = new URLSearchParams({
      address: query,
      language: 'pt',
      key: GOOGLE_KEY,
    });
    if (country2) {
      gp.set('region', country2.toLowerCase());
      gp.set('components', `country:${country2}`);
    }
    providers.push(
      fetch(`https://maps.googleapis.com/maps/api/geocode/json?${gp}`)
        .then(r => r.ok ? r.json() : { results: [] })
        .then(data => (data.results || []).slice(0, 8).map(r => {
          const cc = (r.address_components || [])
            .find(c => Array.isArray(c.types) && c.types.includes('country'))
            ?.short_name?.toUpperCase() || null;
          // Quando o tipo é "street_address" / "premise" / "subpremise",
          // o resultado é parcel-level — exactamente o que queremos.
          // Os tipos "route" (rua sem número) e "locality" são úteis
          // mas menos detalhados; mantemos na mesma para casos em que
          // a query só dá nome de rua.
          const isParcel = (r.types || []).some(t =>
            ['street_address', 'premise', 'subpremise', 'point_of_interest'].includes(t)
          );
          return {
            name: r.formatted_address,
            coordinates: [r.geometry.location.lng, r.geometry.location.lat],
            source: 'google-geo',
            country: cc,
            isParcel,
          };
        }))
        .catch(() => [])
    );
  }

  const results = await Promise.all(providers);
  const all = results.flat();

  // Deduplicate by proximity (~150m). When two providers return the
  // same physical place, prefer the one with a more specific label —
  // Google's POI results (e.g. "Pastelaria Versailles · R. da Junqueira")
  // beat a generic Mapbox/OSM "Lisboa, Portugal".
  const sourceRank = { google: 0, mapbox: 1, here: 2, osm: 3 };
  const unique = [];
  for (const item of all) {
    const dupeIdx = unique.findIndex(u =>
      Math.abs(u.coordinates[0] - item.coordinates[0]) < 0.0015 &&
      Math.abs(u.coordinates[1] - item.coordinates[1]) < 0.0015
    );
    if (dupeIdx === -1) {
      unique.push(item);
      continue;
    }
    const existing = unique[dupeIdx];
    // Replace if the new candidate has a richer label OR comes from a
    // higher-priority source (Google > Mapbox > HERE > OSM).
    const itemRank = sourceRank[item.source] ?? 9;
    const exRank = sourceRank[existing.source] ?? 9;
    const itemMoreSpecific = (item.name?.length || 0) > (existing.name?.length || 0) + 8;
    if (itemRank < exRank || itemMoreSpecific) {
      unique[dupeIdx] = item;
    }
  }

  // Ranking final:
  //   1. País correcto (quando há bias) primeiro.
  //   2. Se a query contém um número (provável endereço com porta),
  //      resultados parcel-level (Google Geocoding street_address ou
  //      Mapbox address) vêm à frente dos POIs — o operador queria
  //      uma morada específica, não um restaurante.
  //   3. Senão (query sem números), POIs do Google Places primeiro
  //      (pastelarias, lojas) e depois Geocoder/Mapbox/HERE/OSM.
  const queryHasNumber = /\b\d{1,4}[a-zA-Z]?\b/.test(query);
  const sourceRankAddr = { 'google-geo': 0, mapbox: 1, here: 2, google: 3, osm: 4 };
  const sourceRankPOI  = { google: 0, 'google-geo': 1, mapbox: 2, here: 3, osm: 4 };
  unique.sort((a, b) => {
    const ac = (a.country || '').toUpperCase() === country2 ? 0 : 1;
    const bc = (b.country || '').toUpperCase() === country2 ? 0 : 1;
    if (ac !== bc) return ac - bc;
    // Parcel-level (porta exacta) ganha sempre quando há número na query.
    if (queryHasNumber) {
      const ap = a.isParcel ? 0 : 1;
      const bp = b.isParcel ? 0 : 1;
      if (ap !== bp) return ap - bp;
    }
    const rankMap = queryHasNumber ? sourceRankAddr : sourceRankPOI;
    const ar = rankMap[a.source] ?? 9;
    const br = rankMap[b.source] ?? 9;
    return ar - br;
  });
  return unique.slice(0, 10);
}

/**
 * Mock route for when no Mapbox token is configured
 */
function mockRoute(origin, destination) {
  // Generate a straight-line mock route between two points
  const dx = destination[0] - origin[0];
  const dy = destination[1] - origin[1];
  const distanceDeg = Math.sqrt(dx * dx + dy * dy);
  const distanceM = distanceDeg * 111000; // ~111km per degree
  const durationS = distanceM / 25; // ~90 km/h average

  // Create a simple line geometry with a few intermediate points
  const steps = 20;
  const coordinates = Array.from({ length: steps + 1 }, (_, i) => [
    origin[0] + (dx * i) / steps,
    origin[1] + (dy * i) / steps,
  ]);

  return {
    distance: distanceM,
    duration: durationS,
    geometry: {
      type: 'LineString',
      coordinates,
    },
    legs: [{
      distance: distanceM,
      duration: durationS,
      summary: 'Mock route (no Mapbox token)',
    }],
  };
}
